import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import type { Pagination } from '@/shared/lib';
import type { SearchParams } from '@/shared/lib';

import { search, type PaginatedSearchApiResult } from '@/entities/Document/api';
import { useCustomToast } from '@/shared/lib';
import { shouldShowErrorToast } from '@/shared/lib/';

export function createDocumentRevisionsStore<T extends { id: number }>(storeId: string) {
  return defineStore(storeId, () => {
    const revisions = ref<T[]>([]) as Ref<T[]>;
    const allRevisions = ref<T[]>([]) as Ref<T[]>;
    const metadata = ref<Pagination>();
    const loading = ref(false);
    const loadingMore = ref(false);
    const hasMoreRevisions = ref(true);
    const currentPage = ref(1);
    const selectedQuantity = ref(10);
    const searchParams = ref<SearchParams | null>(null);
    const lastSearchKey = ref('');

    const toast = useCustomToast();
    const batchSize = 30;

    const hasResults = computed(() => revisions.value?.length > 0);
    const isInfiniteScrollMode = computed(() => selectedQuantity.value === 100);
    const shouldShowTable = computed(() => {
      if (!searchParams.value) return false;
      const hasFilters = Object.values(searchParams.value.filter).some((value) => !!value);
      const hasSort = searchParams.value.sort?.sort || searchParams.value.sort?.order;
      return hasResults.value || hasFilters || hasSort;
    });

    const generateSearchKey = (params: SearchParams): string => {
      return JSON.stringify({
        search: params.search,
        searchType: params.searchType,
        sort: params.sort,
        filter: params.filter,
        amount: params.amount,
        searchUrl: params.searchUrl,
        context: params.context,
        page: params.page,
      });
    };

    const shouldProceedWithSearch = (append: boolean): boolean => {
      if (append && loadingMore.value) return false;
      if (append && !hasMoreRevisions.value) return false;
      return true;
    };

    const initializeSearchState = (append: boolean, searchKey: string) => {
      if (append) {
        loadingMore.value = true;
        return;
      }

      loading.value = true;
      if (searchKey !== lastSearchKey.value) {
        resetSearchState('newSearch');
      }
    };

    const prepareSearchParams = (params: SearchParams, page: number) => {
      return {
        ...params,
        page,
        amount: isInfiniteScrollMode.value ? batchSize : params.amount,
      };
    };

    const updateRevisionsAfterSearch = (response: PaginatedSearchApiResult<T>, append: boolean) => {
      if (isInfiniteScrollMode.value) {
        allRevisions.value = append
          ? [...allRevisions.value, ...response.data]
          : [...response.data];
        hasMoreRevisions.value = response.data.length === batchSize;
      }

      revisions.value = append ? revisions.value : response.data;
      metadata.value = response.metadata;
    };

    const handleSearchError = (error: unknown, append: boolean) => {
      console.error('Error searching revisions:', error);

      if (shouldShowErrorToast(error)) {
        toast.showErrorToast({
          title: 'Erro ao buscar revisões',
          message: 'Tente novamente mais tarde.',
          type: 'error',
        });
        return;
      }

      if (!append) {
        revisions.value = [];
        allRevisions.value = [];
        metadata.value = {
          total: 0,
          page: 1,
          totalPages: 0,
          isNextPage: false,
          isPreviousPage: false,
        };
      }
    };

    const performSearch = async (params: SearchParams, page = 1, append = false) => {
      if (!shouldProceedWithSearch(append)) return;

      const searchKey = generateSearchKey(params);
      initializeSearchState(append, searchKey);

      try {
        const searchParamsWithPage = prepareSearchParams(params, page);
        const response = await search<PaginatedSearchApiResult<T>>(
          searchParamsWithPage.searchUrl,
          searchParamsWithPage
        );

        updateRevisionsAfterSearch(response, append);
        searchParams.value = params;
        lastSearchKey.value = searchKey;

        if (!append) {
          currentPage.value = page;
        }
      } catch (error) {
        handleSearchError(error, append);
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };

    const loadMoreRevisions = async () => {
      if (!searchParams.value || loadingMore.value || !hasMoreRevisions.value) return;

      currentPage.value += 1;
      await performSearch(searchParams.value, currentPage.value, true);
    };

    const setQuantity = (quantity: number) => {
      selectedQuantity.value = quantity;
      currentPage.value = 1;
      allRevisions.value = [];
      hasMoreRevisions.value = true;
    };

    const removeRevisions = (deletedIds: number[]) => {
      const initialCount = revisions.value.length;

      revisions.value = revisions.value.filter((rev) => !deletedIds.includes(rev.id));
      allRevisions.value = allRevisions.value.filter((rev) => !deletedIds.includes(rev.id));

      const removedCount = initialCount - revisions.value.length;
      const messages: string[] = [];

      if (removedCount > 0) {
        messages.push(
          `${removedCount} documento${removedCount > 1 ? 's' : ''} removido${removedCount > 1 ? 's' : ''} da lista`
        );
      }

      if (metadata.value) {
        metadata.value.total = Math.max(0, metadata.value.total - deletedIds.length);
      }

      return {
        deletedIds,
        messages,
        count: removedCount,
      };
    };

    const resetSearchState = (level: 'full' | 'refresh' | 'newSearch') => {
      currentPage.value = 1;
      hasMoreRevisions.value = true;

      if (level === 'refresh' || level === 'newSearch' || level === 'full') {
        allRevisions.value = [];
      }

      if (level === 'newSearch' || level === 'full') {
        revisions.value = [];
      }

      if (level === 'full') {
        metadata.value = undefined;
        searchParams.value = null;
        lastSearchKey.value = '';
      }
    };

    const clearCache = () => {
      resetSearchState('full');
    };

    const refreshCurrentSearch = async () => {
      if (searchParams.value) {
        resetSearchState('refresh');
        await performSearch(searchParams.value);
      }
    };

    return {
      revisions,
      allRevisions,
      metadata,
      loading,
      loadingMore,
      hasMoreRevisions,
      currentPage,
      selectedQuantity,
      searchParams,

      hasResults,
      isInfiniteScrollMode,
      shouldShowTable,

      performSearch,
      loadMoreRevisions,
      setQuantity,
      removeRevisions,
      clearCache,
      refreshCurrentSearch,
    };
  });
}
